import type { TFunction } from 'i18next'

import { ApiError } from '@/lib/api'
import { describeProblem } from '@/lib/problems'
import { rateMissingMessage } from '@/lib/refusals'

/* The case screen's own refusal wording, next to the case screen rather than in
 * `lib/refusals.ts`. That module is the DOCUMENT pipeline's sentence — the FR-64
 * branding refusal three features share — and this one is the case's two named
 * 409s. Neither is a superset of the other, so they stay two functions; this one
 * lives here because the sections that need it are no longer one file. */

/* A 409 whose names the reader has to see, translated rather than echoed.
 *
 * This used to return `error.problem.detail` verbatim for conflicts — the one
 * place in the client that rendered the server's English developer-facing member,
 * held open because FR-67's leg refusal and FR-25's close refusal must NAME what
 * blocks them and `detail` was the only thing carrying the names. That was a hole
 * in AD-27 kept open by two endpoints: a Russian user got English, or the generic
 * sentence with no names in it at all.
 *
 * Both refusals now carry the names in `problem.errors` (`expenses`, `balances`),
 * the shape Story 5.3's branding refusal established and directory's reference
 * refusal widened to a list of records. So the FRAME is translated here and the
 * names print inside it, which is what makes the Russian sentence read as a
 * sentence rather than as a label over English prose (UX-DR-30).
 *
 * Anything else falls through to the ordinary suffix mapping. A conflict carrying
 * no list is still a conflict, and there is nothing to interpolate — which is
 * exactly the answer a caller whose money view does not reach the case gets
 * (story B3 fix round 2): the same 409 with no `errors` member and a fixed
 * English `detail` that is never echoed. */
export function refusalMessage(error: unknown, t: TFunction): string {
  /* First: a cost line or a carrier debt in a currency with no cached rate is
     the money write's commonest refusal on an old case, and only its own
     members say whether to wait or to ask an administrator. */
  const rate = rateMissingMessage(error, t)
  if (rate !== null) return rate
  if (error instanceof ApiError && error.code === 'conflict') {
    const named = error.problem.errors
    if (named?.expenses?.length) {
      return t('cases.legs.carriesExpenses', { items: named.expenses.join('; ') })
    }
    if (named?.balances?.length) {
      return t('cases.lifecycle.unsettledBalances', { items: named.balances.join('; ') })
    }
  }
  return t(describeProblem(error).detailKey)
}
